import { stripTypename } from "@apollo/client/utilities";
import {
  Callout,
  Card,
  ControlGroup,
  FormGroup,
  InputGroup,
  Intent,
  Navbar,
  NavbarGroup,
  NumericInput,
  ProgressBar,
  Switch,
  Tab,
  type TabId,
  Tabs,
} from "@blueprintjs/core";
import {
  getComponentHelperText,
  getComponentIntent,
  ItemMultiSelect,
  ItemSelect,
  MediaImage,
  type SaveToolbarProps,
} from "@package/ui-apollo-components";
import { HeaderCreateEdit } from "@package/ui-components";
import {
  CreatePostDocument,
  DeletePostDocument,
  GetPostDocument,
  GetPostGridDocument,
  GetPostListFullDocument,
  GetPostListNameIdDocument,
  GetServiceListNameIdDocument,
  type GetServiceListNameIdQuery,
  type GetServiceListNameIdQueryVariables,
  GetTagListNameIdDocument,
  type Post,
  ServiceTypes,
  UpdatePostDocument,
  useGetPostQuery,
} from "@package/ui-gql/index-data-access";
import { useUpdateSelectedOrganization } from "@package/ui-organization";
import type { ValidationError } from "class-validator";
import { produce, type WritableDraft } from "immer";
import { get, set, uniq } from "lodash";
import { useCallback, useEffect, useState } from "react";
import {
  EDIT_CARD_WIDE_WIDTH_CLASS,
  EDIT_CARD_WIDTH_CLASS,
  SidebarEntryPath,
} from "../../../../constants/contants";
import { MediaTagSelector } from "../../../MediaTagSelector/MediaTagSelector";
import { SaveToolbar } from "../../../SaveToolbar/SaveToolbar";
import { ServiceDataForm } from "../../../ServiceDataForm/ServiceDataForm";
import { TagCreateDrawer } from "../tag/TagEditDrawer";
import { PostAdvancedQrCodeListEditor } from "./PostAdvancedQrCodeListEditor";
import { PostCreateDrawer } from "./PostEditDrawer";
import { PostQrCodeCanvas } from "./PostQrCodeCanvas";
import { PostTileListEditor } from "./PostTileListEditor";
import { PostTriviaPreview } from "./PostTriviaPreview";

// The Trivia flyout's Service id (apis/data-access/.../flyout-trivia.ts). Every other flyout
// component in this app hardcodes this id the same way (e.g. ScreenFlyout.tsx's dispatch map)
// rather than plumbing it through GraphQL, so this matches existing convention.
const TRIVIA_SERVICE_ID = "66a9203f29411eff6ca74e5b";

const defaultPost: Post = {
  customData: {},
  enabled: true,
  flyoutShowCloseButton: true,
  flyoutType: null,
  hideFromSearch: false,
  organization: "",
  qrCodeLink: null,
  qrCodeLocation: null,
  qrCodeLocationPercent: null,
  qrCodeStyle: null,
  relatedPostAsset: null,
  relatedPosts: [],
  staleFlyoutTimeout: null,
  tags: [],
  tileAssets: [
    {
      customData: {},
      layout: null,
      media: null,
      qrLinkPrefix: null,
      tileBackingImage: null,
      tileType: null,
    },
  ],
};

enum PostTabId {
  flyout = "flyout",
  general = "general",
  qrCode = "qrCode",
}

type PostEditProps = {
  id?: string;
  toolbarProps?: Partial<SaveToolbarProps>;
};

export function PostEdit({ id, toolbarProps }: PostEditProps) {
  const [postState, setPostState] = useState<Post>(defaultPost);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [selectedTabId, setSelectedTabId] = useState<TabId>(PostTabId.general);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const isTriviaFlyout = postState.flyoutType === TRIVIA_SERVICE_ID;

  const updatePost = useCallback(
    (callback: (draft: WritableDraft<Post>) => void) => {
      setPostState((prev) => produce(prev, callback));
    },
    [],
  );

  const {
    data: dataPost,
    loading: loadingPost,
    error: errorPost,
  } = useGetPostQuery({
    variables: {
      id: id ?? "",
    },
  });

  useEffect(
    function effectOnPostDataChanged() {
      if (dataPost?.item) {
        setPostState(dataPost.item);
      }
    },
    [dataPost?.item],
  );

  useUpdateSelectedOrganization(dataPost?.item?.organization);

  if (loadingPost) {
    return (
      <Card elevation={1}>
        <ProgressBar intent="primary" stripes animate />
      </Card>
    );
  }

  if (errorPost) {
    return (
      <Callout intent="danger" title="Failed Loading Content">
        <p>Failed loading content item: {id}</p>
      </Callout>
    );
  }

  return (
    <div className="flex flex-col">
      <HeaderCreateEdit
        editing={!!id}
        loading={loadingPost}
        title="Content"
        name={postState.title}
      />
      <Navbar className="mt-4">
        <NavbarGroup>
          <Tabs
            animate
            large
            onChange={(tabId) => setSelectedTabId(tabId)}
            selectedTabId={selectedTabId}
          >
            <Tab
              id={PostTabId.general}
              className="outline-none"
              title="General"
            />
            <Tab
              id={PostTabId.flyout}
              className="outline-none"
              title="Interactive"
            />
            <Tab
              id={PostTabId.qrCode}
              className="outline-none"
              title="QR Code"
            />
          </Tabs>
        </NavbarGroup>
      </Navbar>
      {selectedTabId === PostTabId.general && (
        <>
          <Card elevation={1} className="mt-4">
            <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
              <FormGroup
                label="Name"
                labelFor="control-name"
                labelInfo="(required)"
                disabled={disabled}
                intent={getComponentIntent<Post>("title", errors)}
                helperText={getComponentHelperText<Post>("title", errors)}
              >
                <InputGroup
                  disabled={disabled}
                  id="control-name"
                  placeholder="Name"
                  value={postState.title ?? ""}
                  onValueChange={(value) =>
                    updatePost((draft) => {
                      draft.title = value;
                    })
                  }
                />
              </FormGroup>
              <FormGroup
                disabled={disabled}
                intent={getComponentIntent<Post>("enabled", errors)}
                helperText={getComponentHelperText<Post>("enabled", errors)}
              >
                <Switch
                  disabled={disabled}
                  label="Enabled"
                  checked={!!postState.enabled}
                  onChange={() =>
                    updatePost((draft) => {
                      draft.enabled = !draft.enabled;
                    })
                  }
                />
              </FormGroup>
              <FormGroup
                disabled={disabled}
                intent={getComponentIntent<Post>("hideFromSearch", errors)}
                helperText={getComponentHelperText<Post>(
                  "hideFromSearch",
                  errors,
                )}
              >
                <Switch
                  disabled={disabled}
                  label="Hide From Search"
                  checked={!!postState.hideFromSearch}
                  onChange={() =>
                    updatePost((draft) => {
                      draft.hideFromSearch = !draft.hideFromSearch;
                    })
                  }
                />
              </FormGroup>
            </div>
          </Card>
          <Card className="mt-4">
            <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
              <FormGroup
                label="Tags"
                disabled={disabled}
                intent={getComponentIntent<Post>("tags", errors)}
                helperText={getComponentHelperText<Post>("tags", errors)}
              >
                <ItemMultiSelect
                  document={GetTagListNameIdDocument}
                  activeItemIds={postState.tags}
                  createDrawerElement={TagCreateDrawer}
                  disabled={disabled}
                  onItemSelected={(id) => {
                    updatePost((draft) => {
                      if (id) {
                        draft.tags = draft.tags || [];
                        draft.tags = uniq(draft.tags.concat([id]));
                      }
                    });
                  }}
                  onItemRemoved={(id) => {
                    updatePost((draft) => {
                      if (id) {
                        draft.tags = draft.tags?.filter((l) => l !== id);
                      }
                    });
                  }}
                />
              </FormGroup>
              <FormGroup
                label="Related Content"
                disabled={disabled}
                intent={getComponentIntent<Post>("relatedPosts", errors)}
                helperText={getComponentHelperText<Post>(
                  "relatedPosts",
                  errors,
                )}
              >
                <ItemMultiSelect
                  createDrawerElement={PostCreateDrawer}
                  document={GetPostListNameIdDocument}
                  activeItemIds={postState.relatedPosts}
                  disabled={disabled}
                  onItemSelected={(id) => {
                    updatePost((draft) => {
                      if (id) {
                        draft.relatedPosts = draft.relatedPosts || [];
                        draft.relatedPosts = uniq(
                          draft.relatedPosts.concat([id]),
                        );
                      }
                    });
                  }}
                  onItemRemoved={(id) => {
                    updatePost((draft) => {
                      if (id) {
                        draft.relatedPosts = draft.relatedPosts?.filter(
                          (l) => l !== id,
                        );
                      }
                    });
                  }}
                />
              </FormGroup>
              <FormGroup
                disabled={disabled}
                label="Related Content Thumbnail"
                intent={getComponentIntent<Post>("relatedPostAsset", errors)}
                helperText={getComponentHelperText<Post>(
                  "relatedPostAsset",
                  errors,
                )}
              >
                <MediaTagSelector
                  id={postState.relatedPostAsset ?? undefined}
                  onRemoveMedia={() => {
                    updatePost((draft) => {
                      draft.relatedPostAsset = undefined;
                    });
                  }}
                  onSelectMedia={(mediaId) => {
                    updatePost((draft) => {
                      draft.relatedPostAsset = mediaId;
                    });
                  }}
                />
              </FormGroup>
              {!!postState.relatedPostAsset && (
                <MediaImage id={postState.relatedPostAsset} size="max-h-48" />
              )}
            </div>
          </Card>
          <Card className="mt-4">
            <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
              <PostTileListEditor
                onTilesChanged={(tiles) =>
                  updatePost((draft) => {
                    draft.tileAssets = tiles;
                  })
                }
                disabled={disabled}
                tiles={postState.tileAssets ?? []}
              />
            </div>
          </Card>
        </>
      )}
      {selectedTabId === PostTabId.flyout && (
        <>
          <Card className="mt-4">
            <div
              className={isTriviaFlyout ? "max-w-7xl" : EDIT_CARD_WIDTH_CLASS}
            >
              <div className={isTriviaFlyout ? "flex gap-6" : undefined}>
                <div className={isTriviaFlyout ? "min-w-0 flex-1" : undefined}>
                  <FormGroup
                    disabled={disabled}
                    label="Interactive Type"
                    intent={getComponentIntent<Post>("flyoutType", errors)}
                    helperText={getComponentHelperText<Post>(
                      "flyoutType",
                      errors,
                    )}
                  >
                    <ItemSelect<
                      GetServiceListNameIdQuery,
                      GetServiceListNameIdQueryVariables
                    >
                      activeItemId={postState.flyoutType}
                      disabled={disabled}
                      document={GetServiceListNameIdDocument}
                      nullable
                      nullableText="None"
                      onItemSelected={(flyoutTypeId) => {
                        updatePost((draft) => {
                          draft.flyoutType = flyoutTypeId;

                          // Trivia's welcome screen defaults to shown, but its descriptor
                          // (flyout-trivia.ts) can't say so — a boolean's CMS defaultValue is
                          // display-only and never persisted. Seeding it here, only for a post
                          // that doesn't exist yet, is what keeps every already-authored trivia
                          // post rendering exactly as it does today.
                          if (
                            !id &&
                            flyoutTypeId === TRIVIA_SERVICE_ID &&
                            get(draft.customData, "trivia.welcome.enabled") ===
                              undefined
                          ) {
                            if (!draft.customData) {
                              draft.customData = {};
                            }
                            set(
                              draft.customData,
                              "trivia.welcome.enabled",
                              true,
                            );
                          }
                        });
                      }}
                      variables={{
                        filter: {
                          type: ServiceTypes.Flyout,
                        },
                      }}
                    />
                  </FormGroup>
                  {!!postState.flyoutType && (
                    <>
                      <FormGroup
                        intent={getComponentIntent<Post>("flyoutScale", errors)}
                        helperText={getComponentHelperText<Post>(
                          "flyoutScale",
                          errors,
                          "The visual scale multiplier of the interactive content. Default value is 1.0",
                        )}
                        label="Interactive Scale"
                      >
                        <NumericInput
                          disabled={disabled}
                          min={0}
                          allowNumericCharactersOnly
                          stepSize={0.1}
                          fill
                          value={postState.flyoutScale ?? undefined}
                          onValueChange={(value, valueAsString) => {
                            updatePost((draft) => {
                              draft.flyoutScale =
                                valueAsString.length === 0 ? null : value;
                            });
                          }}
                        />
                      </FormGroup>
                      <FormGroup
                        disabled={disabled}
                        label="No Interaction Timeout"
                        fill
                        intent={getComponentIntent(
                          "staleFlyoutTimeout",
                          errors,
                        )}
                        helperText={getComponentHelperText(
                          "staleFlyoutTimeout",
                          errors,
                          "The amount of time in seconds before a flyout will close from no user input. (Overrides layout value)",
                        )}
                      >
                        <NumericInput
                          disabled={disabled}
                          min={0}
                          allowNumericCharactersOnly
                          fill
                          value={postState.staleFlyoutTimeout ?? undefined}
                          onValueChange={(value) => {
                            updatePost((draft) => {
                              draft.staleFlyoutTimeout = value;
                            });
                          }}
                        />
                      </FormGroup>
                      <FormGroup
                        disabled={disabled}
                        intent={getComponentIntent<Post>(
                          "flyoutShowCloseButton",
                          errors,
                        )}
                        helperText={getComponentHelperText<Post>(
                          "flyoutShowCloseButton",
                          errors,
                        )}
                      >
                        <Switch
                          disabled={disabled}
                          label="Show Close Button"
                          checked={!!postState.flyoutShowCloseButton}
                          onChange={() =>
                            updatePost((draft) => {
                              draft.flyoutShowCloseButton =
                                !draft.flyoutShowCloseButton;
                            })
                          }
                        />
                      </FormGroup>
                      <FormGroup helperText="(Overrides positions set in the Canvas)">
                        <Switch
                          disabled={disabled}
                          label="Set interactive content at specific position"
                          checked={!!postState.flyoutPosition}
                          onChange={() =>
                            updatePost((draft) => {
                              draft.flyoutPosition = draft.flyoutPosition
                                ? undefined
                                : {
                                    x: 0,
                                    y: 0,
                                    width: 0,
                                    height: 0,
                                  };
                            })
                          }
                        />
                      </FormGroup>
                      {postState.flyoutPosition && (
                        <ControlGroup fill>
                          <FormGroup disabled={disabled} label="Interative (x)">
                            <NumericInput
                              min={0}
                              disabled={disabled}
                              allowNumericCharactersOnly
                              fill
                              value={postState.flyoutPosition?.x ?? undefined}
                              onValueChange={(value) => {
                                updatePost((draft) => {
                                  draft.flyoutPosition = {
                                    ...draft.flyoutPosition,
                                    x: value,
                                  };
                                });
                              }}
                            />
                          </FormGroup>
                          <FormGroup
                            className="ml-4"
                            disabled={disabled}
                            label="Interative (y)"
                          >
                            <NumericInput
                              min={0}
                              disabled={disabled}
                              allowNumericCharactersOnly
                              fill
                              value={postState.flyoutPosition?.y ?? undefined}
                              onValueChange={(value) => {
                                updatePost((draft) => {
                                  draft.flyoutPosition = {
                                    ...draft.flyoutPosition,
                                    y: value,
                                  };
                                });
                              }}
                            />
                          </FormGroup>
                          <FormGroup
                            className="ml-4"
                            disabled={disabled}
                            label="Width"
                          >
                            <NumericInput
                              min={0}
                              disabled={disabled}
                              allowNumericCharactersOnly
                              fill
                              value={
                                postState.flyoutPosition?.width ?? undefined
                              }
                              onValueChange={(value) => {
                                updatePost((draft) => {
                                  draft.flyoutPosition = {
                                    ...draft.flyoutPosition,
                                    width: value,
                                  };
                                });
                              }}
                            />
                          </FormGroup>
                          <FormGroup
                            className="ml-4"
                            disabled={disabled}
                            label="Height"
                          >
                            <NumericInput
                              min={0}
                              disabled={disabled}
                              allowNumericCharactersOnly
                              fill
                              value={
                                postState.flyoutPosition?.height ?? undefined
                              }
                              onValueChange={(value) => {
                                updatePost((draft) => {
                                  draft.flyoutPosition = {
                                    ...draft.flyoutPosition,
                                    height: value,
                                  };
                                });
                              }}
                            />
                          </FormGroup>
                        </ControlGroup>
                      )}
                      {getComponentIntent<Post>("customData", errors) ===
                        Intent.DANGER && (
                        <Callout intent="danger" className="mb-4">
                          {getComponentHelperText<Post>("customData", errors)}
                        </Callout>
                      )}
                      <FormGroup
                        intent={getComponentIntent<Post>("flyoutType", errors)}
                        helperText={getComponentHelperText<Post>(
                          "flyoutType",
                          errors,
                        )}
                      >
                        <ServiceDataForm
                          id={postState.flyoutType}
                          appCmsType="post"
                          card={false}
                          data={postState}
                          onSourceChanged={(callback) =>
                            updatePost((draft) => callback(draft))
                          }
                          showTitle={false}
                        />
                      </FormGroup>
                    </>
                  )}
                </div>
                {isTriviaFlyout && (
                  <div className="sticky top-4 w-[32rem] flex-none self-start">
                    <PostTriviaPreview post={postState} />
                  </div>
                )}
              </div>
            </div>
          </Card>
        </>
      )}
      {selectedTabId === PostTabId.qrCode && (
        <>
          <Card className="mt-4">
            <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
              <FormGroup
                className="mb-8"
                label="QR Code Link"
                disabled={disabled}
                intent={getComponentIntent<Post>("qrCodeLink", errors)}
                helperText={getComponentHelperText<Post>("qrCodeLink", errors)}
              >
                <InputGroup
                  disabled={disabled}
                  placeholder="QR Code"
                  value={postState.qrCodeLink ?? ""}
                  onValueChange={(value) =>
                    updatePost((draft) => {
                      draft.qrCodeLink = value;
                    })
                  }
                />
              </FormGroup>
              <FormGroup>
                <PostAdvancedQrCodeListEditor
                  disabled={disabled}
                  codes={postState.qrCodeLinkByLocationProfile ?? []}
                  onCodeChanged={(codes) =>
                    updatePost((draft) => {
                      draft.qrCodeLinkByLocationProfile = codes;
                    })
                  }
                />
              </FormGroup>
            </div>
          </Card>
          <Card className="mt-4">
            <div className={`${EDIT_CARD_WIDE_WIDTH_CLASS}`}>
              <PostQrCodeCanvas
                disabled={disabled}
                post={postState}
                errors={errors}
                updatePost={updatePost}
              />
            </div>
          </Card>
        </>
      )}
      <Card className="mt-4">
        <div className={`flex ${EDIT_CARD_WIDTH_CLASS}`}>
          <SaveToolbar
            documentCreate={CreatePostDocument}
            documentDelete={DeletePostDocument}
            documentUpdate={UpdatePostDocument}
            path={SidebarEntryPath.posts}
            isNew={!id}
            refetchQueries={[
              GetPostDocument,
              GetPostGridDocument,
              GetPostListNameIdDocument,
              GetPostListFullDocument,
            ]}
            title="Content"
            validationModel="post-update"
            variablesCreate={stripTypename(postState)}
            variablesUpdate={stripTypename(postState)}
            variablesDelete={{ id }}
            {...toolbarProps}
            onInflight={(inflight) => {
              setDisabled(inflight);

              if (inflight) {
                setErrors([]);
              }
            }}
            onValidationFailed={(errors) => {
              setErrors(errors);
            }}
          />
        </div>
      </Card>
    </div>
  );
}
